/**
 * Fragment Grow Shortcut — phrase matcher for "grow this compound",
 * "try fragment growing", "find modifications", etc.
 *
 * Triggers an exhaustive fragment grow search across all eligible bond
 * vectors on the active compound. Results (new fragment-grown compounds)
 * appear in the workspace's compound panel.
 *
 * **Requires a fragment map.** Fragment growing operates on pre-computed
 * Boltzmann Maps fragment simulation data. If no fragment map is loaded
 * for the active protein, the shortcut returns a graceful degradation
 * message explaining how to get one.
 *
 * The matcher lives here (no BMaps deps); execution lives in ChatBot.jsx
 * where App and UserActions are available.
 *
 * @module gibbs/knowledge/fragmentGrowShortcut
 */

/**
 * Phrases that trigger fragment growing.
 */
// Keep TIGHT — canonical phrases only. The LLM handles fuzzy variants.
const FRAGMENT_GROW_PHRASES = [
    'fragment grow',
    'fragment growing',
    'grow this compound',
    'grow the compound',
];

/**
 * @param {string} userMessage
 * @returns {boolean}
 */
export function matchFragmentGrowIntent(userMessage) {
    if (!userMessage || typeof userMessage !== 'string') return false;
    const lower = userMessage.toLowerCase();
    return FRAGMENT_GROW_PHRASES.some((p) => lower.includes(p));
}

/**
 * Check workspace preconditions for fragment growing.
 * Returns {ready: true} or {ready: false, message: string} with
 * a human-readable explanation of what's missing.
 *
 * @param {Object} workspace - App.Workspace
 * @returns {{ready: boolean, message?: string}}
 */
export function checkFragmentGrowPreconditions(workspace) {
    if (!workspace) {
        return { ready: false, message: 'No workspace available.' };
    }

    // Protein loaded?
    if (typeof workspace.firstCaseData === 'function') {
        const cd = workspace.firstCaseData();
        if (!cd || (typeof cd.getSolute === 'function' && !cd.getSolute())) {
            return {
                ready: false,
                message: 'No protein is loaded. Load a protein first, then try again.',
            };
        }
    }

    // Compound loaded?
    if (typeof workspace.getLoadedCompounds === 'function') {
        const compounds = workspace.getLoadedCompounds();
        if (!Array.isArray(compounds) || compounds.length === 0) {
            return {
                ready: false,
                message: 'No compounds are loaded. Load or draw a compound first, then try again.',
            };
        }
    }

    // Fragment map available?
    if (typeof workspace.firstCaseData === 'function') {
        const cd = workspace.firstCaseData();
        if (cd) {
            const fragInfo = cd.getAvailableFragmentInfo?.();
            const hasFrags = fragInfo && typeof fragInfo.items === 'function'
                && fragInfo.items().length > 0;
            if (!hasFrags) {
                return {
                    ready: false,
                    message: 'No fragment map is available for this protein. '
                        + 'Fragment growing requires pre-computed Boltzmann Maps '
                        + 'fragment simulation data.\n\n'
                        + 'To get a fragment map:\n'
                        + '- Go to **Modeling Tasks** → **Run Frag. Sim.** to start '
                        + 'a fragment simulation (takes ~30 minutes)\n'
                        + '- Or load a protein that already has simulation data '
                        + '(e.g. SIRT1/4RMG)\n\n'
                        + 'Once a fragment map is loaded, come back and say '
                        + '"grow this compound" to search for modifications.',
                };
            }
        }
    }

    return { ready: true };
}
